import { Effect, Schema as S } from "effect";

/**
 * Wire shapes for contact import. These mirror the server's request and
 * response schemas exactly: the commit contact is a *closed* schema, so a
 * stray key fails the write for the whole chunk.
 */

export const ImportSource = S.Literals(["linkedin", "google", "vcard"]);
export type ImportSource = typeof ImportSource.Type;

export const ImportIdentity = S.Struct({
	system: S.Literals(["linkedin", "email"]),
	value: S.String,
});

/* ---------------------------------------------------------------- match ---- */

/** One row's match keys. Everything but `rowId` is optional; the file's bytes never leave the browser. */
export const MatchRow = S.Struct({
	rowId: S.Number,
	identity: S.optional(ImportIdentity),
	emailNormalized: S.optional(S.String),
	nameKey: S.optional(S.String),
});
export type MatchRow = typeof MatchRow.Type;

/** At most this many rows per `/import/match` call. */
export const MATCH_CHUNK = 500;

export const MatchCandidate = S.Struct({
	recordId: S.String,
	name: S.String,
	organisation: S.optional(S.String),
	reason: S.Literals(["email", "name"]),
});
export type MatchCandidate = typeof MatchCandidate.Type;

/** An identity hit sets `alreadyInCircle` and returns no candidates. */
export const MatchResult = S.Struct({
	rowId: S.Number,
	alreadyInCircle: S.optional(S.String),
	candidates: S.Array(MatchCandidate),
});
export type MatchResult = typeof MatchResult.Type;

export const MatchResponse = S.Struct({
	circleId: S.String,
	remainingRecordCapacity: S.Number,
	results: S.Array(MatchResult),
});
export type MatchResponse = typeof MatchResponse.Type;

/* --------------------------------------------------------------- commit ---- */

/** Closed schema: no batch, provenance, or parser bookkeeping. See `toImportContact`. */
export const ImportContact = S.Struct({
	name: S.String,
	organisation: S.optional(S.String),
	position: S.optional(S.String),
	emails: S.Array(S.String),
	phones: S.Array(S.String),
	location: S.optional(S.String),
	note: S.optional(S.String),
	date: S.optional(S.String),
	labels: S.Array(S.String),
	identity: S.optional(ImportIdentity),
	vcardUid: S.optional(S.String),
});
export type ImportContact = typeof ImportContact.Type;

export const ImportRow = S.Struct({
	rowId: S.Number,
	action: S.Literals(["create", "update"]),
	targetRecordId: S.optional(S.String),
	contact: ImportContact,
});
export type ImportRow = typeof ImportRow.Type;

/** At most this many rows per `/import/commit` call. */
export const COMMIT_CHUNK = 25;

/** `notAttempted` follows a `terminal` failure: the server stopped the chunk. */
export const CommitResult = S.Struct({
	rowId: S.Number,
	outcome: S.Literals(["created", "updated", "failed", "notAttempted"]),
	recordId: S.optional(S.String),
	reason: S.optional(S.String),
	terminal: S.optional(S.Boolean),
});
export type CommitResult = typeof CommitResult.Type;

export const CommitResponse = S.Struct({ results: S.Array(CommitResult) });
export type CommitResponse = typeof CommitResponse.Type;

/* ----------------------------------------------------------------- jobs ---- */

/** The whole plan in one request; the server runs it as a background job. Idempotent on `batchId`. */
export const ImportJobSubmitRequest = S.Struct({
	batchId: S.String,
	circleId: S.String,
	source: ImportSource,
	expectedRows: S.Number,
	skippedRows: S.Number,
	rows: S.Array(ImportRow),
});
export type ImportJobSubmitRequest = typeof ImportJobSubmitRequest.Type;

export const ImportJobState = S.Literals([
	"running",
	"done",
	"stopped",
	"failed",
]);
export type ImportJobState = typeof ImportJobState.Type;

/** The 202 body: the job's first status, before any row has landed. */
export const ImportJobAccepted = S.Struct({
	batchId: S.String,
	state: ImportJobState,
	landed: S.Number,
	total: S.Number,
});
export type ImportJobAccepted = typeof ImportJobAccepted.Type;

/** `results` is the server's whole list so far, never a delta. */
export const ImportJobStatus = S.Struct({
	...ImportJobAccepted.fields,
	results: S.Array(CommitResult),
	stoppedReason: S.optional(S.String),
});
export type ImportJobStatus = typeof ImportJobStatus.Type;

/* -------------------------------------------------------------- batches ---- */

export const BatchState = S.Literals([
	"incomplete",
	"complete",
	"stopped",
	"undone",
]);
export type BatchState = typeof BatchState.Type;

export const ImportBatch = S.Struct({
	batchId: S.String,
	source: ImportSource,
	datetime: S.String,
	expectedRows: S.Number,
	created: S.Number,
	updated: S.Number,
	entries: S.Number,
	skippedRows: S.Number,
	state: BatchState,
});
export type ImportBatch = typeof ImportBatch.Type;

export const ImportBatches = S.Struct({ batches: S.Array(ImportBatch) });
export type ImportBatches = typeof ImportBatches.Type;

/* ----------------------------------------------------------------- undo ---- */

export const UndoPreview = S.Struct({
	created: S.Number,
	updated: S.Number,
	editedSince: S.Number,
	withEntries: S.Number,
	mergedAway: S.Number,
	missing: S.Number,
});
export type UndoPreview = typeof UndoPreview.Type;

export const UndoCounts = S.Struct({
	recordsDeleted: S.Number,
	entriesDeleted: S.Number,
	recordsReverted: S.Number,
	recordsKept: S.Number,
});
export type UndoCounts = typeof UndoCounts.Type;

const NO_COUNTS: UndoCounts = {
	recordsDeleted: 0,
	entriesDeleted: 0,
	recordsReverted: 0,
	recordsKept: 0,
};

/**
 * Undo is chunked: the caller re-posts `cursor` until `done`. An
 * already-undone batch answers `{done: true, state: "undone"}` with no
 * counts, which decodes to zeros so a caller can always add them up.
 */
export const UndoResponse = S.Struct({
	cursor: S.optional(S.Number),
	done: S.Boolean,
	state: S.optional(S.Literals(["undone", "inProgress"])),
	counts: UndoCounts.pipe(S.withDecodingDefaultKey(Effect.succeed(NO_COUNTS))),
});
export type UndoResponse = typeof UndoResponse.Type;
